import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Delete,
  Add,
  VolumeUp,
  Download,
  ContentCopy,
} from "@mui/icons-material";
import axios from "axios";
import { apiURL } from "../constant";

const LessonEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [wordForms, setWordForms] = useState([]);
  const [previewWords, setPreviewWords] = useState([]);
  const [wordInputs, setWordInputs] = useState([
    { word: "", meaning: "", type: "", pronunciation: "" },
  ]);
  const [batchInputOpen, setBatchInputOpen] = useState(false);
  const [batchInput, setBatchInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);

  useEffect(() => {
    const savedLessons = JSON.parse(localStorage.getItem("lessons") || "[]");
    const currentLesson = savedLessons.find((l) => l.id === id);
    if (currentLesson) {
      setLesson(currentLesson);
      setWordForms(
        currentLesson.words.map((word) => ({
          word: word.word || "",
          meaning: word.meaning || "",
          pronunciation: word.pronunciation || "",
          type: word.type || "",
        }))
      );
      setWordInputs(
        currentLesson.words.map((word) => ({
          word: word.word,
          meaning: word.meaning,
          type: word.type,
          pronunciation: word.pronunciation || "",
        }))
      );
    } else {
      navigate("/");
    }
  }, [id, navigate]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      // Lọc bỏ các từ trống
      const validWords = wordInputs.filter(
        (input) => input.word.trim() && input.meaning.trim()
      );

      if (validWords.length === 0) {
        console.log("No valid words to save");
        return;
      }

      // Gọi API để lấy IPA và audioUrl cho tất cả từ
      const wordsWithPronunciation = await Promise.all(
        validWords.map(async (word) => {
          try {
            const response = await axios.get(
              `${apiURL}/api/audio?word=${word.word}`
            );
            return {
              word: word.word.trim(),
              meaning: word.meaning.trim(),
              type: word.type.trim(),
              pronunciation: response.data?.ipa || word.pronunciation || "",
              audioUrl: response.data?.audioUrl || word.audioUrl || "",
            };
          } catch (error) {
            console.error(
              `Error fetching pronunciation for ${word.word}:`,
              error
            );
            return {
              word: word.word.trim(),
              meaning: word.meaning.trim(),
              type: word.type.trim(),
              pronunciation: word.pronunciation || "",
              audioUrl: word.audioUrl || "",
            };
          }
        })
      );

      const updatedLesson = {
        ...lesson,
        words: wordsWithPronunciation,
      };

      const savedLessons = JSON.parse(localStorage.getItem("lessons") || "[]");
      const updatedLessons = savedLessons.map((l) =>
        l.id === id ? updatedLesson : l
      );

      localStorage.setItem("lessons", JSON.stringify(updatedLessons));

      // Chuyển hướng sau khi lưu thành công
      navigate(`/lesson/${id}`);
    } catch (error) {
      console.error("Error saving lesson:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    const validWords = wordForms.filter(
      (form) => form.word && form.meaning && form.pronunciation && form.type
    );
    setPreviewWords(validWords);
  };

  const handlePlayAudio = async (word) => {
    try {
      const response = await axios.get(`${apiURL}/api/audio?word=${word}`);
      if (response.data?.audioUrl) {
        const audio = new Audio(response.data.audioUrl);
        audio.play();
      }
    } catch (error) {
      console.error("Error playing audio:", error);
    }
  };

  const handleAddWord = () => {
    setWordInputs([
      ...wordInputs,
      { word: "", meaning: "", type: "", pronunciation: "" },
    ]);
  };

  const handleRemoveWord = (index) => {
    const newInputs = wordInputs.filter((_, i) => i !== index);
    setWordInputs(newInputs);
  };

  const handleWordChange = (index, field, value) => {
    const newInputs = [...wordInputs];
    newInputs[index][field] = value;
    setWordInputs(newInputs);
  };

  const parseInput = (input) => {
    const lines = input.split("\n").filter((line) => line.trim());
    const words = lines.map((line) => {
      // Format: từ (loại từ) nghĩa /phiên âm/
      const firstBracketIndex = line.indexOf("(");
      const lastBracketIndex = line.indexOf(")");
      const lastSlashIndex = line.lastIndexOf("/");

      if (
        firstBracketIndex !== -1 &&
        lastBracketIndex !== -1 &&
        lastSlashIndex !== -1
      ) {
        const word = line.substring(0, firstBracketIndex).trim();
        const type = line
          .substring(firstBracketIndex + 1, lastBracketIndex)
          .trim();

        // Tìm vị trí của dấu / đầu tiên sau dấu ngoặc đơn
        let firstSlashIndex = line.indexOf("/", lastBracketIndex);
        if (firstSlashIndex === -1) return null;

        // Lấy phần nghĩa từ sau dấu ngoặc đơn đến trước dấu /
        const meaning = line
          .substring(lastBracketIndex + 1, firstSlashIndex)
          .trim();
        const pronunciation = line
          .substring(firstSlashIndex + 1, lastSlashIndex)
          .trim();

        return {
          word: word,
          type: type,
          meaning: meaning,
          pronunciation: pronunciation,
        };
      }
      return null;
    });
    return words.filter((word) => word !== null);
  };

  const handleBatchAdd = () => {
    const newWords = parseInput(batchInput);
    if (newWords.length > 0) {
      setWordInputs([...wordInputs, ...newWords]);
      setBatchInput("");
      setBatchInputOpen(false);
    }
  };

  const handleExportScript = () => {
    const validWords = wordInputs.filter(
      (input) => input.word.trim() && input.meaning.trim()
    );

    const script = validWords
      .map((word) => {
        return `${word.word} (${word.type}) ${word.meaning} /${word.pronunciation}/`;
      })
      .join("\n");

    // Tạo file và tải xuống
    const blob = new Blob([script], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${lesson?.name || "lesson"}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleShowScript = () => {
    setBatchInput(
      wordInputs
        .filter((word) => word.word.trim() && word.meaning.trim())
        .map(
          (word) =>
            `${word.word} (${word.type}) ${word.meaning} /${word.pronunciation}/`
        )
        .join("\n")
    );
    setScriptDialogOpen(true);
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(batchInput);
  };

  if (!lesson) return null;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton
            onClick={() => navigate(`/lesson/${lesson.id}`)}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Chỉnh sửa: {lesson.name}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setBatchInputOpen(true)}
            sx={{ mr: 2 }}
          >
            Thêm hàng loạt
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleShowScript}
            sx={{ mr: 2 }}
          >
            Xuất script
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isSaving ? "Đang lưu..." : "Lưu thay đổi"}
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Nhập từ vựng
            </Typography>
            {wordInputs.map((input, index) => (
              <Box key={index} sx={{ mb: 2, display: "flex", gap: 2 }}>
                <TextField
                  label="Từ"
                  value={input.word}
                  onChange={(e) =>
                    handleWordChange(index, "word", e.target.value)
                  }
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Nghĩa"
                  value={input.meaning}
                  onChange={(e) =>
                    handleWordChange(index, "meaning", e.target.value)
                  }
                  sx={{ flex: 3 }}
                />
                <TextField
                  label="Loại từ"
                  value={input.type}
                  onChange={(e) =>
                    handleWordChange(index, "type", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Phiên âm"
                  value={input.pronunciation}
                  onChange={(e) =>
                    handleWordChange(index, "pronunciation", e.target.value)
                  }
                  sx={{ flex: 2 }}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveWord(index)}
                  sx={{ alignSelf: "center" }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddWord}
              fullWidth
              sx={{ mt: 2 }}
            >
              Thêm từ
            </Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              sx={{ mt: 2, ml: 2 }}
            >
              Xem trước
            </Button>
          </Box>

          {previewWords.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Xem trước
              </Typography>
              <Grid container spacing={2}>
                {previewWords.map((word, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            {word.word}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handlePlayAudio(word.word)}
                          >
                            <VolumeUp />
                          </IconButton>
                        </Box>
                        <Typography color="text.secondary" gutterBottom>
                          {word.pronunciation}
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {word.meaning}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {word.type}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={batchInputOpen}
        onClose={() => setBatchInputOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Thêm từ hàng loạt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Nhập từ theo định dạng: từ (loại từ) nghĩa /phiên âm/
            <br />
            Ví dụ:
            <br />
            hello (n) xin chào /həˈloʊ/
            <br />
            world (n) thế giới /wɜːld/
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={batchInput}
            onChange={(e) => setBatchInput(e.target.value)}
            placeholder="hello (n) xin chào /həˈloʊ/
world (n) thế giới /wɜːld/"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchInputOpen(false)}>Hủy</Button>
          <Button onClick={handleBatchAdd} variant="contained">
            Thêm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Script từ vựng</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={batchInput}
            InputProps={{
              readOnly: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyScript}>Sao chép</Button>
          <Button onClick={() => setScriptDialogOpen(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LessonEdit;
